import { KINDS, type ConceptKind, type ParamValue } from '../concepts/kinds';
import { translate } from '../i18n/i18n';

/**
 * A program as the API now returns it.
 *
 * WHAT WENT AWAY: gameType, tileCount, uniqueWordCount, words and promptType.
 * Those were one kind's parameters occupying the shape every kind has to share
 * — a timed hold has seconds and a number of holds, a supervisor-scored trial
 * has a list of prompts and nothing to count. They live in `config` now, under
 * the names the kind itself declares.
 */
/** Which of the two lists a program sits on. */
export type ProgramTrack = 'CURRENT' | 'MAINTENANCE';

export type ProgramView = {
  id: string;
  learnerId: string;
  name: string;
  kind: string;
  category: string | null;
  track: ProgramTrack;
  /**
   * Everything specific to this kind.
   *
   * AN ABSENT KEY MEANS "FOLLOW MY DEFAULT" and that is load bearing: it is
   * what lets a supervisor change one setting and move every program that never
   * disagreed with it. Writing every parameter in at creation would freeze each
   * program at the value it was born with.
   */
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type ProgramDraft = {
  name: string;
  kind: ConceptKind;
  track: ProgramTrack;
  config: Record<string, unknown>;
};

/** Whether the client knows how to render this kind at all. */
export function isKnownKind(kind: string): kind is ConceptKind {
  return Object.prototype.hasOwnProperty.call(KINDS, kind);
}

/**
 * One line describing a program, built from whatever the kind declares.
 *
 * WRITTEN GENERICALLY ON PURPOSE. This used to read
 * `{gameType} · {uniqueWordCount} words · {tileCount} tiles`, which is a
 * sentence only a tile match can complete. A kind with no tiles produced
 * "undefined words · undefined tiles".
 */
export function describeProgram(p: ProgramView): string {
  if (!isKnownKind(p.kind)) return translate('Unknown kind of game');
  const def = KINDS[p.kind];
  const parts: string[] = [translate(def.label)];

  /* COUNTS ROWS, NOT WORDS. The third place carrying the flat-word-list
     assumption, and the one the typechecker found rather than I did: content is
     pairs of a prompt and the tile that answers it, so "4 words" was describing
     a shape that no longer exists. */
  if (def.content.type === 'pairs') {
    const rows = Array.isArray(p.config.items) ? p.config.items.length : 0;
    parts.push(translate('{n} to teach', { n: rows }));
  }

  /* Only what this program actually states. A program following every default
     says so, rather than listing values it does not own. */
  const overridden = def.params.filter((param) => p.config[param.id] !== undefined).length;
  parts.push(overridden === 0
    ? translate('all settings default')
    : overridden === 1
      ? translate('{n} setting set', { n: overridden })
      : translate('{n} settings set', { n: overridden }));

  return parts.join(' · ');
}

/** The value a field should show: what the program states, or nothing. */
export function statedValue(config: Record<string, unknown>, id: string): ParamValue | undefined {
  const v = config[id];
  if (v === undefined || v === null) return undefined;
  return v as ParamValue;
}
